import { useState } from 'react'

import { ActionIcon, Menu, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { useMutation } from '@redwoodjs/web'

import DnTable from 'src/components/Core/DnTable'
import AvatarGroup from 'src/components/DataDisplay/AvatarGroup/AvatarGroup'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { CANCEL_DATA_MIGRATION } from 'src/graphql/dataMigrations.graphql'
import IconCopy from 'src/icons/IconCopy'
import IconThreeDots from 'src/icons/IconThreeDots'
import { formatDateCustom } from 'src/lib/formatters'
import {
  formatProgress,
  formatProviderLabel,
  isStalled,
  isTerminalPhase,
} from 'src/pages/SettingsPage/tabs/dataMigrationTab/lib/migrationDisplay'
import { DataMigrationRow } from 'src/pages/SettingsPage/tabs/dataMigrationTab/types'

import MigrationDetailDrawer from './MigrationDetailDrawer'
import MigrationPhaseBadge from './MigrationPhaseBadge'

/** Day, month, year and time — e.g. "Aug 11, 2026 9:47 AM". */
const DATE_TIME_FORMAT = 'MMM D, YYYY h:mm A'

interface MigrationsTableProps {
  migrations: DataMigrationRow[]
  loading: boolean
  /** Refresh the list after a migration is cancelled. */
  onCancelled: () => void
}

const MigrationsTable = ({
  migrations,
  loading,
  onCancelled,
}: MigrationsTableProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [migrationToCancel, setMigrationToCancel] =
    useState<DataMigrationRow | null>(null)
  const [
    cancelModalOpened,
    { open: openCancelModal, close: closeCancelModal },
  ] = useDisclosure(false)

  const [cancelDataMigration, { loading: cancelling }] = useMutation(
    CANCEL_DATA_MIGRATION,
    {
      onCompleted: () => {
        toast('Migration cancelled', 'success')
        onCancelled()
      },
      onError: (e) =>
        toast(e?.message || 'Unable to cancel migration', 'error'),
    }
  )

  const handleCopyId = (migrationId: string) => {
    navigator.clipboard.writeText(migrationId)
    toast('Migration ID copied to clipboard', 'success')
  }

  const confirmCancelMigration = async () => {
    if (!migrationToCancel) return
    await cancelDataMigration({
      variables: { dataMigrationId: migrationToCancel.dataMigrationId },
    })
  }

  // Derive from the list so the drawer stays fresh while polling refreshes rows.
  const selectedMigration =
    migrations.find((m) => m.dataMigrationId === selectedId) ?? null

  const columns = [
    {
      key: 'migrationId',
      label: 'ID',
      width: 5,
      // Icon only — the generated ID is long and noisy in a cell. The full
      // value is on hover and one click from the clipboard.
      render: (migration: DataMigrationRow) => (
        <Tooltip label={migration.migrationId} withArrow>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            aria-label={`Copy ID ${migration.migrationId}`}
            onClick={(e) => {
              // The row opens the detail drawer — copying must not.
              e.stopPropagation()
              handleCopyId(migration.migrationId)
            }}
          >
            <IconCopy className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Tooltip>
      ),
    },
    {
      key: 'provider',
      label: 'Type',
      width: 10,
      render: (migration: DataMigrationRow) => (
        <div className="text-xs text-doubleNickel-gray-700">
          {formatProviderLabel(migration.provider)}
        </div>
      ),
    },
    {
      key: 'phase',
      label: 'Phase',
      width: 18,
      render: (migration: DataMigrationRow) => (
        <MigrationPhaseBadge
          phase={migration.phase}
          errorMessage={migration.errorMessage}
          stalled={isStalled(migration)}
        />
      ),
    },
    {
      key: 'progress',
      label: 'Progress',
      width: 12,
      render: (migration: DataMigrationRow) => (
        <div className="text-xs text-doubleNickel-gray-700">
          {formatProgress(migration)}
        </div>
      ),
    },
    {
      key: 'initiatedBy',
      label: 'Initiated by',
      width: 10,
      render: (migration: DataMigrationRow) =>
        migration.initiatedBy ? (
          // Shared avatar treatment — the tooltip carries the full name.
          <AvatarGroup
            avatars={[
              {
                firstName: migration.initiatedBy.firstName ?? '',
                lastName: migration.initiatedBy.lastName ?? '',
              },
            ]}
          />
        ) : (
          <div className="text-xs text-doubleNickel-gray-600">—</div>
        ),
    },
    {
      key: 'createdAt',
      label: 'Started',
      width: 14,
      render: (migration: DataMigrationRow) => (
        <div className="whitespace-nowrap text-xs text-doubleNickel-gray-600">
          {formatDateCustom(migration.createdAt, DATE_TIME_FORMAT)}
        </div>
      ),
    },
    {
      key: 'lastActivity',
      label: 'Last activity',
      width: 14,
      render: (migration: DataMigrationRow) => {
        const stalled = isStalled(migration)
        return (
          <div className="flex flex-row items-center gap-1.5 whitespace-nowrap text-xs text-doubleNickel-gray-600">
            {formatDateCustom(
              migration.lastHeartbeatAt ?? migration.updatedAt,
              DATE_TIME_FORMAT
            )}
            {stalled && (
              <Tooltip
                label="No heartbeat for over 30 minutes — this migration may be stalled"
                withArrow
              >
                <span
                  data-testid="last-activity-stalled"
                  className="h-2 w-2 rounded-full bg-doubleNickel-warning-400"
                />
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      key: 'actions',
      label: '',
      width: 6,
      render: (migration: DataMigrationRow) =>
        isTerminalPhase(migration.phase) ? null : (
          <div className="flex items-center justify-end">
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  className="h-6 w-6"
                  aria-label="Migration actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconThreeDots className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="w-44">
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  disabled={cancelling}
                  onClick={(e) => {
                    e.stopPropagation()
                    setMigrationToCancel(migration)
                    openCancelModal()
                  }}
                >
                  Cancel migration
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        ),
    },
  ]

  return (
    <>
      <DnTable
        columns={columns}
        data={migrations}
        loading={loading}
        enablePagination={false}
        emptyStateMessage={
          <div className="flex flex-col items-center gap-1 py-4">
            <span className="text-sm font-medium text-doubleNickel-gray-700">
              No migrations yet
            </span>
            <span className="text-sm text-doubleNickel-gray-500">
              Use Add Migration to start one.
            </span>
          </div>
        }
        onRowClick={(_rowIndex, migration: DataMigrationRow) =>
          setSelectedId(migration.dataMigrationId)
        }
        rowClassName="cursor-pointer hover:bg-doubleNickel-gray-50"
      />

      <MigrationDetailDrawer
        opened={!!selectedId}
        onClose={() => setSelectedId(null)}
        migration={selectedMigration}
      />

      <DeleteModal
        title="Cancel Migration"
        description="Cancel this migration? This stops Double Nickel from tracking it. A running AWS task will keep going until it finishes."
        opened={cancelModalOpened}
        close={closeCancelModal}
        onDelete={confirmCancelMigration}
      />
    </>
  )
}

export default MigrationsTable
